import React, { useState, Fragment } from 'react';
import { useTranslate, Button, useRefresh } from 'react-admin';
import { SvgIcon, DialogContent, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import { useMutation } from 'react-query';
import { EditButton } from '~/components';
import { Business, Individual } from '~/types';
import { resetKycProgress } from '~/api/kyc';
import { useNotify } from '~/hooks';
import { ShieldCheckmarkIcon } from '~/img';
import { eventLogUpdate } from '~/utils';
import { getProfileTypeByResource } from '~/utils/profileType';

const label = (resource: string, key: string): string => `resources.${resource}.fields.${key}`;

interface Props {
  record: Individual | Business | undefined;
  resource: string;
}

const KycResetProgress = (props: Props) => {
  const { record, resource } = props;

  const [open, setOpen] = useState(false);
  const closeDialog = () => setOpen(false);
  const openDialog = () => setOpen(true);

  const translate = useTranslate();
  const t = (key: string): string => translate(label(resource, key));

  const notify = useNotify();

  const forceRefresh = useRefresh();

  const { mutateAsync: resetKycProgressAsync, isLoading } = useMutation(resetKycProgress);

  const profileType = getProfileTypeByResource(resource);
  const handleReset = () => {
    if (!record || (profileType !== 'INDIVIDUAL' && profileType !== 'BUSINESS')) return null;

    return resetKycProgressAsync({ profileType, profileId: record.id }).then(() => {
      notify({ message: t('kycResetInitiated'), type: 'info' });
      forceRefresh();
      eventLogUpdate(resource);
      closeDialog();
    });
  };

  return (
    <Fragment>
      <EditButton
        label={t('kycResetProgress')}
        icon={
          <SvgIcon>
            <ShieldCheckmarkIcon />
          </SvgIcon>
        }
        onClick={openDialog}
      />
      <Dialog open={open} onClose={closeDialog} aria-labelledby='form-dialog-title'>
        <DialogTitle>{t('kycResetProgress')}</DialogTitle>
        <DialogContent>
          <Alert severity='warning'>{t('kycResetProgressWarning')}</Alert>
        </DialogContent>
        <DialogActions>
          <Button label='ra.action.cancel' onClick={closeDialog} />
          <Button
            label={t('kycResetProgress')}
            variant='contained'
            onClick={handleReset}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={18} />
            ) : (
              <SvgIcon>
                <ShieldCheckmarkIcon />
              </SvgIcon>
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default KycResetProgress;
